import RadioButtonControlGroup from "../../../../../shared/RadioButtonControlGroup";
import SelectField from "../../../../common/SelectField";

/**
 * Plan-type radio options for the Plan-affiliation tab.
 * @readonly
 */
export const AFFILIATION_PLAN_TYPE_OPTIONS = Object.freeze([
    { value: "Javelina", label: "Javelina" },
    { value: "HRP", label: "HRP" },
]);

import "./PlanAffiliationTab.scss";

/**
 * Plan-affiliation tab: the three-step flow of picking a plan type, a client
 * ID, and then a network per plan. Presentational — selections flow up via
 * callbacks; owns no state.
 *
 * @param {object} props
 * @param {Object} props.planAffiliation - `{ planType, selectedClientId, plans }`.
 * @param {Array<{ id: string, label: string }>} props.clientOptions - Clients for the selected plan type.
 * @param {Array<{ title: string, value: string }>} props.networkNameOptions - Network choices per plan.
 * @param {(planType: string) => void} props.onPlanTypeChange
 * @param {(clientId: string) => void} props.onClientIdChange
 * @param {(planId: string, networkValue: string) => void} props.onNetworkAffiliationChange
 * @returns {import('react').JSX.Element}
 */
const PlanAffiliationTab = ({
    planAffiliation,
    clientOptions,
    networkNameOptions,
    onPlanTypeChange,
    onClientIdChange,
    onNetworkAffiliationChange,
}) => {
    const { planType, selectedClientId, plans } = planAffiliation;

    return (
        <div
            id="config-panel-planAffiliation"
            role="tabpanel"
            aria-labelledby="config-tab-planAffiliation"
            className="config-plan-affiliation-tab"
        >
            <div className="config-plan-affiliation-tab__step">
                <span className="config-plan-affiliation-tab__step-number">
                    1
                </span>
                <p>Select the plan type</p>
            </div>
            <RadioButtonControlGroup
                ariaLabel="Plan type"
                value={planType || null}
                onChange={onPlanTypeChange}
                options={AFFILIATION_PLAN_TYPE_OPTIONS}
            />

            {planType ? (
                <>
                    <div className="config-plan-affiliation-tab__step">
                        <span className="config-plan-affiliation-tab__step-number">
                            2
                        </span>
                        <p>
                            Select the client ID to affiliate the configuration
                            to the plan
                        </p>
                    </div>
                    <SelectField
                        id="affiliation-client-id"
                        label="Client ID"
                        className="config-plan-affiliation-tab__client"
                        value={selectedClientId}
                        onChange={onClientIdChange}
                        placeholderOption="Select a client ID"
                        options={clientOptions.map((client) => ({
                            value: client.id,
                            label: client.label,
                            disabled: client.disabled,
                        }))}
                    />
                </>
            ) : null}

            {selectedClientId ? (
                <>
                    <div className="config-plan-affiliation-tab__step">
                        <span className="config-plan-affiliation-tab__step-number">
                            3
                        </span>
                        <p>Select a network name to affiliate to each plan ID</p>
                    </div>
                    <div className="config-plan-affiliation-tab__table">
                        <div className="config-plan-affiliation-tab__table-head">
                            <span>PLAN ID</span>
                            <span>NETWORK NAME</span>
                        </div>
                        {plans.map((plan) => (
                            <div
                                className="config-plan-affiliation-tab__row"
                                key={plan.planId}
                            >
                                <span className="config-plan-affiliation-tab__plan-id">
                                    {plan.planId}
                                </span>
                                <select
                                    aria-label={`Network for plan ${plan.planId}`}
                                    value={plan.networkAffiliation}
                                    onChange={(event) =>
                                        onNetworkAffiliationChange(
                                            plan.planId,
                                            event.target.value,
                                        )
                                    }
                                >
                                    <option value="">Select a network</option>
                                    {networkNameOptions.map((network) => (
                                        <option
                                            key={network.value}
                                            value={network.value}
                                        >
                                            {network.title}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        ))}
                    </div>
                </>
            ) : null}
        </div>
    );
};

export default PlanAffiliationTab;
